import React, { useCallback, useMemo, useRef } from 'react';

import { Button } from '@appcharge/shared-ui';
import usePermissions from 'api/usePermissions';
import { useUserDetails } from 'api/useUserDetails';
import { permissionsUtil } from 'utils/permissionsUtil';
import { getSelectProjectOptions } from 'utils/projectsUtil';

import { FilterPopover } from 'components/FilterComponents/FilterPopover/FilterPopover';
import { FilterType } from 'components/FilterComponents/FilterPopover/types';

import {
  getCreatedDateFilterDisplay,
  getSortFilterBannerLabel,
  reportsSortFilterOptions,
  reportsStatusFilterOptions,
  serializeCreatedDateFilterValue,
  tryParseDateRangeFromFilter
} from '../../utils';

import { ReportsFilterBarProps } from './ReportsFilterBar.types';

const ReportsFilterBarComponent: React.FC<ReportsFilterBarProps> = ({
  clearFiltersButton,
  handleClearFilters,
  sortDirection,
  onSortDirectionChange,
  selectedProjects,
  onProjectChange,
  selectedStatuses,
  onStatusChange,
  createdAfter,
  createdBefore,
  onCreatedRangeChange
}) => {
  const { getPermissions } = usePermissions();
  const permissions = getPermissions();
  const allowedPublishers = useMemo(
    () => permissionsUtil.getPublishersByFinancialReports(permissions),
    [permissions]
  );
  const { userDetails } = useUserDetails();
  const projectsDetails = userDetails?.projects;

  const projectFilterOptions = useMemo(() => {
    return projectsDetails && allowedPublishers.length > 0
      ? getSelectProjectOptions(projectsDetails, allowedPublishers).map(
          (project) => ({
            label: project.content as string,
            value: project.value
          })
        )
      : [];
  }, [projectsDetails, allowedPublishers]);

  const currentDateValue = useMemo(
    () => serializeCreatedDateFilterValue(createdAfter, createdBefore),
    [createdAfter, createdBefore]
  );

  const { displayLabel, displayParts } = useMemo(
    () => getCreatedDateFilterDisplay(createdAfter, createdBefore),
    [createdAfter, createdBefore]
  );

  const onCreatedRangeChangeRef = useRef(onCreatedRangeChange);
  onCreatedRangeChangeRef.current = onCreatedRangeChange;

  const handleDateChange = useCallback((value: string) => {
    if (!value) {
      onCreatedRangeChangeRef.current('', '');
      return;
    }
    const parsed = tryParseDateRangeFromFilter(value);
    if (parsed) {
      onCreatedRangeChangeRef.current(parsed.startDate, parsed.endDate);
    }
  }, []);

  return (
    <div className="flex flex-row gap-1 items-center flex-wrap">
      <FilterPopover
        label="Project"
        filterType={FilterType.AUTOCOMPLETE_CHECKLIST}
        options={projectFilterOptions}
        selectedItems={selectedProjects}
        onChange={onProjectChange}
        placeholder="Search projects..."
        dataTestId="reportsProjectFilter"
      />
      <FilterPopover
        label={getSortFilterBannerLabel(sortDirection)}
        filterType={FilterType.SELECT}
        options={reportsSortFilterOptions}
        selectedItems={[sortDirection]}
        onChange={onSortDirectionChange}
        dataTestId="reportsSortFilter"
      />
      <FilterPopover
        label="Status"
        filterType={FilterType.CHECKLIST}
        options={reportsStatusFilterOptions}
        selectedItems={selectedStatuses}
        onChange={onStatusChange}
        popoverHeight="200px"
        dataTestId="reportsStatusFilter"
      />
      <FilterPopover
        label={displayLabel}
        labelParts={displayParts}
        onChange={handleDateChange}
        selectedItems={currentDateValue ? [currentDateValue] : []}
        filterType={FilterType.DATE_PICKER}
        hideIndicator
        dataTestId="reportsCreatedDateFilter"
      />
      {clearFiltersButton && (
        <Button
          variant="link"
          onClick={handleClearFilters}
          className="text-indigo-600 hover:text-base-primary text-xs border-none shadow-none"
        >
          Clear filters
        </Button>
      )}
    </div>
  );
};

export const ReportsFilterBar = React.memo(ReportsFilterBarComponent);
ReportsFilterBar.displayName = 'ReportsFilterBar';
